// Notification Service for managing popups and marquee
export class NotificationService {
  static COOKIE_PREFIX = 'notification_shown_';
  
  // Clear all notification cookies (for testing/debugging)
  static clearAllNotificationCookies() {
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name.startsWith(this.COOKIE_PREFIX)) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        // console.log('Cleared cookie:', name);
      }
    });
  }
  
  // Set cookie for popup/marquee tracking
  // We store the timestamp (ms) when the notification was shown as the cookie value.
  // The cookie expiration is also set according to the provided hours so the browser
  // will eventually remove it, but the authoritative check is done by comparing the
  // stored timestamp against the dynamic cookieTime in `shouldShowNotification`.
  static setCookie(notificationId, hours = 24) {
    // Ensure hours is a number and handle edge cases
    const numHours = Number.isFinite(Number(hours)) ? parseInt(hours, 10) : 24;
    const now = Date.now();
    const expiryDate = new Date(now + (numHours * 60 * 60 * 1000));

    const cookieName = `${this.COOKIE_PREFIX}${notificationId}`;
    // store the timestamp in milliseconds as the cookie value
    const cookieValue = String(now);
    const cookieString = `${cookieName}=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/`;

    // console.log('Setting cookie (timestamped):', {
    //   notificationId,
    //   hours: numHours,
    //   setAt: new Date(now).toUTCString(),
    //   expiryDate: expiryDate.toUTCString(),
    //   cookieString
    // });

    document.cookie = cookieString;
  }
  
  // Check if cookie exists for notification
  static getCookie(notificationId) {
    const name = `${this.COOKIE_PREFIX}${notificationId}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  }
  
  // Check if notification is valid based on validity date
  static isValidDate(validityDate) {
    if (!validityDate) return true;
    
    const today = new Date();
    const validity = new Date(validityDate);
    return validity >= today;
  }
  
  // Check if notification should be shown
  // This uses the dynamic `cookie_time` (or `cookieTime`) from the notification object
  // to decide whether an existing cookie means "already shown" or has expired.
  static shouldShowNotification(notification) {
    const id = notification.id || notification._id;
    const validity = notification.validity;
    // Get dynamic cookie time from notification data (in hours)
    const cookieTime = Number.isFinite(Number(notification.cookie_time))
      ? Number(notification.cookie_time)
      : (Number.isFinite(Number(notification.cookieTime)) ? Number(notification.cookieTime) : 24);

    const cookieValue = this.getCookie(id);

    const now = Date.now();
    const hasValidCookie = (() => {
      if (!cookieValue) return false;
      // cookieValue should be the timestamp when it was set (ms). If it's not numeric,
      // treat as "shown" to be safe.
      const setAt = parseInt(cookieValue, 10);
      if (Number.isNaN(setAt)) return true;
      const elapsedMs = now - setAt;
      const allowedMs = cookieTime * 60 * 60 * 1000;
      return elapsedMs < allowedMs;
    })();

    // console.log('Checking if notification should show:', {
    //   id,
    //   validity,
    //   cookieTime,
    //   cookieValue,
    //   hasValidCookie,
    //   isValidDate: this.isValidDate(validity)
    // });

    // Check validity date
    if (!this.isValidDate(validity)) {
      // console.log('Notification expired:', id);
      return false;
    }

    // If cookie exists and is still within cookieTime window, do not show
    if (hasValidCookie) {
      // console.log('Notification already shown (valid cookie):', id);
      return false;
    }

    // If cookie exists but expired according to dynamic cookieTime, remove it and allow showing
    if (cookieValue && !hasValidCookie) {
      // console.log('Cookie exists but expired for notification, removing cookie and allowing show:', id);
      // Remove cookie by setting expiry in the past
      document.cookie = `${this.COOKIE_PREFIX}${id}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    return true;
  }
  
  // Mark notification as shown
  static markAsShown(notification) {
    const id = notification.id || notification._id;
    // Get dynamic cookie time from notification data
    const cookieTime = notification.cookie_time || notification.cookieTime || 24;
    // console.log('Marking notification as shown (using dynamic cookieTime):', { id, cookieTime });
    this.setCookie(id, cookieTime);
  }
  
  // Filter notifications by page and type
  static filterNotifications(notifications, pageName, pageType) {
    // console.log('Filtering notifications:', {
    //   notifications,
    //   pageName,
    //   pageType
    // });
    
    const filtered = notifications.filter(notification => {
      const nPageName = notification.pageName || notification.pagename || notification.page;
      const nPageType = notification.pageType || notification.type;
      
      // Check if page and type match
      const pageMatches = nPageName === pageName && nPageType === pageType;
      
      // For marquee notifications, only check validity date (always show if valid)
      // For popup notifications, check both validity and cookie status
      let shouldShow;
      if (nPageType === 'marquee') {
        shouldShow = this.isValidDate(notification.validity);
        // console.log('Marquee notification - only checking validity:', {
        //   id: notification.id || notification._id,
        //   validity: notification.validity,
        //   isValid: shouldShow
        // });
      } else {
        shouldShow = this.shouldShowNotification(notification);
      }
      
      // console.log('Checking notification:', {
      //   id: notification.id || notification._id,
      //   nPageName,
      //   nPageType,
      //   pageName,
      //   pageType,
      //   pageMatches,
      //   shouldShow,
      //   finalResult: pageMatches && shouldShow
      // });
      
      return pageMatches && shouldShow;
    });
    
    // console.log('Filtered result:', filtered);
    return filtered;
  }
}